import { WidgetType } from "./types";
import { PresetZone } from "./presets";

/**
 * Rule-based "Smart Layout" generator. Produces a balanced, non-overlapping
 * set of zones from a few high-level choices — NO external AI service is
 * involved (that lives behind the Phase 4 feature flag). The rules encode
 * the requirements in section 8:
 *   • a primary video/image gets the largest area when "highlight" priority
 *   • text ticker → full-width bottom strip
 *   • clock / weather / QR → compact zones in a side rail (a corner column)
 *   • menu boards favour equal columns
 *   • portrait favours vertical stacking, landscape favours columns/sidebars
 *
 * Every coordinate is a percentage of the canvas, rounded to 2 dp, and the
 * regions are carved so nothing ever overlaps or leaves the canvas.
 */

export type PrimaryContent = "video" | "image" | "menu" | "website";
export type LayoutPriority = "balanced" | "highlight";

export interface SmartLayoutInput {
  orientation: "landscape" | "portrait";
  businessType: string;
  primary: PrimaryContent;
  images: number;
  videos: number;
  includeText: boolean;
  includeClock: boolean;
  includeWeather: boolean;
  includeQr: boolean;
  priority: LayoutPriority;
}

interface Rect { x: number; y: number; w: number; h: number; }

const round2 = (n: number) => Math.round(n * 100) / 100;

const primaryWidget = (p: PrimaryContent): WidgetType =>
  p === "menu" ? "menu_board" : p === "website" ? "website" : p;

/** Ordered list of content widgets for the main area, primary type first. */
const buildContentTiles = (input: SmartLayoutInput): WidgetType[] => {
  const tiles: WidgetType[] = [];
  if (input.primary === "menu") tiles.push("menu_board");
  if (input.primary === "website") tiles.push("website");

  const vids = Array<WidgetType>(Math.max(0, input.videos)).fill("video");
  const imgs = Array<WidgetType>(Math.max(0, input.images)).fill("image");
  if (input.primary === "image") tiles.push(...imgs, ...vids);
  else tiles.push(...vids, ...imgs);

  if (tiles.length === 0) tiles.push(primaryWidget(input.primary));
  return tiles;
};

/** Distribute n items into `rows` rows as evenly as possible. */
const rowCounts = (n: number, rows: number): number[] => {
  const base = Math.floor(n / rows);
  const extra = n % rows;
  return Array.from({ length: rows }, (_, i) => base + (i < extra ? 1 : 0));
};

/** Tile `rect` with the given widgets — no gaps, no overlaps. */
const tileArea = (
  rect: Rect,
  tiles: WidgetType[],
  orientation: "landscape" | "portrait",
  highlight: boolean,
): PresetZone[] => {
  const n = tiles.length;
  const out: PresetZone[] = [];
  const label = (t: WidgetType, i: number) => `${cap(t)} ${i + 1}`;

  if (n === 1) {
    out.push(zone(tiles[0], label(tiles[0], 0), rect));
    return out;
  }

  // Hero + supporting: the primary tile takes a large slab, the rest share
  // the remainder along the short axis.
  if (highlight && n >= 2) {
    const rest = tiles.slice(1);
    if (orientation === "landscape") {
      const heroW = rect.w * 0.62;
      out.push(zone(tiles[0], label(tiles[0], 0), { x: rect.x, y: rect.y, w: heroW, h: rect.h }));
      const colX = rect.x + heroW;
      const colW = rect.w - heroW;
      const eachH = rect.h / rest.length;
      rest.forEach((t, i) => out.push(zone(t, label(t, i + 1),
        { x: colX, y: rect.y + i * eachH, w: colW, h: eachH })));
    } else {
      const heroH = rect.h * 0.6;
      out.push(zone(tiles[0], label(tiles[0], 0), { x: rect.x, y: rect.y, w: rect.w, h: heroH }));
      const rowY = rect.y + heroH;
      const rowH = rect.h - heroH;
      const eachW = rect.w / rest.length;
      rest.forEach((t, i) => out.push(zone(t, label(t, i + 1),
        { x: rect.x + i * eachW, y: rowY, w: eachW, h: rowH })));
    }
    return out;
  }

  // Balanced grid. Landscape favours columns; portrait favours rows.
  let rows: number;
  if (orientation === "landscape") {
    rows = n <= 3 ? 1 : n === 4 ? 2 : Math.ceil(n / Math.ceil(Math.sqrt(n)));
  } else {
    rows = n <= 3 ? n : n === 4 ? 2 : Math.ceil(Math.sqrt(n));
  }
  rows = Math.max(1, Math.min(rows, n));
  const counts = rowCounts(n, rows);
  const rowH = rect.h / rows;
  let idx = 0;
  counts.forEach((c, r) => {
    const colW = rect.w / c;
    for (let ci = 0; ci < c; ci++) {
      const t = tiles[idx];
      out.push(zone(t, label(t, idx),
        { x: rect.x + ci * colW, y: rect.y + r * rowH, w: colW, h: rowH }));
      idx++;
    }
  });
  return out;
};

export const generateSmartLayout = (input: SmartLayoutInput): PresetZone[] => {
  const zones: PresetZone[] = [];

  // 1) Bottom ticker (full-width strip).
  const tickerH = input.includeText ? 12 : 0;

  // 2) Side rail for compact widgets (clock / weather / QR).
  const railWidgets: WidgetType[] = [];
  if (input.includeClock) railWidgets.push("clock");
  if (input.includeWeather) railWidgets.push("weather");
  if (input.includeQr) railWidgets.push("qr");
  const railW = railWidgets.length
    ? (input.orientation === "portrait" ? 30 : 22)
    : 0;

  const mainH = 100 - tickerH;
  const mainW = 100 - railW;

  // 3) Main content area.
  const tiles = buildContentTiles(input);
  tileArea({ x: 0, y: 0, w: mainW, h: mainH }, tiles, input.orientation, input.priority === "highlight")
    .forEach((z) => zones.push(z));

  // 4) Rail widgets stacked vertically in the corner column.
  if (railWidgets.length) {
    const eachH = mainH / railWidgets.length;
    railWidgets.forEach((t, i) =>
      zones.push(zone(t, cap(t), { x: mainW, y: i * eachH, w: railW, h: eachH })));
  }

  // 5) Ticker.
  if (tickerH) {
    zones.push(zone("text", "Ticker", { x: 0, y: mainH, w: 100, h: tickerH }));
  }

  return zones.map((z) => ({
    ...z,
    x: round2(z.x), y: round2(z.y), width: round2(z.width), height: round2(z.height),
  }));
};

/** A friendly name for the generated template based on the inputs. */
export const smartLayoutName = (input: SmartLayoutInput): string => {
  const biz = input.businessType && input.businessType !== "general"
    ? `${cap(input.businessType)} ` : "";
  return `${biz}${cap(input.primary)} Layout`;
};

const zone = (widgetType: WidgetType, name: string, r: Rect): PresetZone => ({
  name,
  widgetType,
  x: r.x, y: r.y, width: r.w, height: r.h,
  zIndex: 0,
  config: widgetType === "menu_board" ? { source: "vmpos", columns: 2 } : undefined,
});

const cap = (s: string) =>
  s === "menu_board" ? "Menu" : s === "qr" ? "QR" : s.charAt(0).toUpperCase() + s.slice(1);
